import { SelectToken } from "../models/tokens";
import { RESOURCE_PACKAGE } from "../util";

export function createTokenManifest(account: string, name: string, symbol: string, description: string, infoUrl: string, iconUrl: string, supply: number) {
    let manifest = `
ALLOCATE_GLOBAL_ADDRESS
    Address("${RESOURCE_PACKAGE}")
    "NonFungibleResourceManager"
    AddressReservation("nft_address_reservation")
    NamedAddress("nft_address")
;

ALLOCATE_GLOBAL_ADDRESS
    Address("${RESOURCE_PACKAGE}")
    "FungibleResourceManager"
    AddressReservation("token_address_reservation")
    NamedAddress("token_address")
;

CREATE_NON_FUNGIBLE_RESOURCE_WITH_INITIAL_SUPPLY
    Enum<0u8>()
    Enum<1u8>()
    true
    Enum<0u8>(
        Enum<0u8>(
            Tuple(
                Array<Enum>(),
                Array<Tuple>(),
                Array<Enum>()
            )
        ),
        Enum<0u8>(
            66u8
        ),
        Array<String>()
    )
    Map<NonFungibleLocalId, Tuple>(
        NonFungibleLocalId("#1#") => Tuple(
            Tuple()
        )
    )
    Tuple(
        Enum<0u8>(),
        Enum<0u8>(),
        Enum<0u8>(),
        Enum<0u8>(),
        Enum<0u8>(),
        Enum<0u8>(),
        Enum<0u8>()
    )
    Tuple(
        Map<String, Tuple>(
            "name" => Tuple(
                Enum<1u8>(
                    Enum<0u8>(
                        "${name} - Token Owner Badge"
                    )
                ),
                false
            ),
            "description" => Tuple(
                Enum<1u8>(
                    Enum<0u8>(
                        "The owner badge for the ${name} token"
                    )
                ),
                false
            ),
            "tags" => Tuple(
                Enum<1u8>(
                    Enum<128u8>(
                        Array<String>(
                            "badge"
                        )
                    )
                ),
                false
            ),
            "icon_url" => Tuple(
                Enum<1u8>(
                    Enum<13u8>(
                        "${iconUrl}"
                    )
                ),
                false
            )
        ),
        Map<String, Enum>()
    )
    Enum<1u8>(
		AddressReservation("nft_address_reservation")
	)
;


CREATE_FUNGIBLE_RESOURCE_WITH_INITIAL_SUPPLY
    # Owner role - This gets metadata permissions, and is the default for other permissions
    # Can set as Enum<OwnerRole::Fixed>(access_rule)  or Enum<OwnerRole::Updatable>(access_rule)
    Enum<OwnerRole::Fixed>(
        Enum<2u8>(
            Enum<0u8>(
                Enum<0u8>(
                    Enum<1u8>(
                        NamedAddress("nft_address")
                    )
                )
            )
        )
    )
    false             # Whether the engine should track supply (avoid for massively parallelizable tokens)
    18u8             # Divisibility (between 0u8 and 18u8)
    Decimal("${supply}") # Initial supply
    Tuple(
        None,         # Mint Roles (if None: defaults to DenyAll, DenyAll)
        Some(         # Burn Roles (if None: defaults to DenyAll, DenyAll)
            Tuple(
                None,  # Burner (if None: defaults to Owner)
                None    # Burner Updater (if None: defaults to Owner)
            )
        ),
        None,        # Freeze Roles (if None: defaults to DenyAll, DenyAll)
        None,        # Recall Roles (if None: defaults to DenyAll, DenyAll)
        None,        # Withdraw Roles (if None: defaults to AllowAll, DenyAll)
        None         # Deposit Roles (if None: defaults to AllowAll, DenyAll)
    )
    Tuple(                                                                   # Metadata initialization
        Map<String, Tuple>(
            "name" => Tuple(
                Enum<1u8>(
                    Enum<0u8>(
                        "${name}"
                    )
                ),
                false
            ),
            "symbol" => Tuple(
                Enum<1u8>(
                    Enum<0u8>(
                        "${symbol}"
                    )
                ),
                false
            ),
`
    if (description) {
        manifest += `
            "description" => Tuple(
                Enum<1u8>(
                    Enum<0u8>(
                        "${description}"
                    )
                ),
                false
            ),
`;
    }

    manifest += `
            "tags" => Tuple(
                Enum<1u8>(
                    Enum<128u8>(
                        Array<String>(
                            "token"
                        )
                    )
                ),
                false
            ),
            "icon_url" => Tuple(
                Enum<1u8>(
                    Enum<13u8>(
                        "${iconUrl}"
                    )
                ),
                false
            ),
`;
    if (infoUrl) {
        manifest += `
            "info_url" => Tuple(
                Enum<1u8>(
                    Enum<13u8>(
                        "${infoUrl}"
                    )
                ),
                false
            )
        
`;
    }

    manifest += `
			),
        Map<String, Enum>()
    )
   Enum<1u8>(
		AddressReservation("token_address_reservation")
	)
;

# Depositing the entirety of the initial supply of the newly created resource into our account
# component.
CALL_METHOD
    Address("${account}")
    "deposit_batch"
    Expression("ENTIRE_WORKTOP")
;`

    return manifest;
}

export function createEditTokenManifest(account: string, token: SelectToken, name: string, symbol: string, description: string, infoUrl: string, iconUrl: string, socialUrls: string[], tags: string, tokenOwner: any) {
    let manifest = '';

    if (tokenOwner.nft) {
        manifest += `
CALL_METHOD
	Address("${account}")
    "create_proof_of_non_fungibles"
    Address("${tokenOwner.nft.address}")
    Array<NonFungibleLocalId>(
        NonFungibleLocalId("${tokenOwner.nft.localId}")
    )
;`
    } else {
        manifest += `
CALL_METHOD
	Address("${account}")
    "create_proof_of_amount"
    Address("${tokenOwner.token}")
    Decimal("1")
;`
    }

    if (token.name !== name) {
        manifest += `
SET_METADATA
    Address("${token.address}")
    "name"
    Enum<Metadata::String>(
        "${name}"
    )
;`;
    }

    if (token.symbol !== symbol) {
        manifest += `
SET_METADATA
    Address("${token.address}")
    "symbol"
    Enum<Metadata::String>(
        "${symbol}"
    )
;`;
    }

    if (token.description !== description) {
        manifest += `
SET_METADATA
    Address("${token.address}")
    "description"
    Enum<Metadata::String>(
        "${description}"
    )
;`;
    }

    if (token.iconUrl !== iconUrl) {
        manifest += `
SET_METADATA
    Address("${token.address}")
    "icon_url"
    Enum<Metadata::Url>(
        "${iconUrl}"
    )
;`;
    }

    if (token.infoUrl !== infoUrl) {
        manifest += `
SET_METADATA
    Address("${token.address}")
    "info_url"
    Enum<Metadata::Url>(
        "${infoUrl}"
    )
;`
    }

    if (socialUrls.length) {
        manifest += `
SET_METADATA
    Address("${token.address}")
    "social_urls"
    Enum<141u8>(
        Array<String>(
           ${socialUrls.map(u => `"${u}"`).join(',')}
        )
    )
;`
    }

    if (tags) {
        manifest += `
SET_METADATA
    Address("${token.address}")
    "tags"
    Enum<Metadata::StringArray>(
        Array<String>(
           ${tags.split(',').map(u => `"${u.trim()}"`).join(',')}
        )
    )
; `
    }

    return manifest;
}

export function createBurnTokenManifest(account: string, tokenAddress: string, amount: number, tokenOwner: any) {
    let manifest = '';

    if (tokenOwner.nft) {
        manifest += `
        CALL_METHOD
        Address("${account}")
        "create_proof_of_non_fungibles"
        Address("${tokenOwner.nft.address}")
        Array<NonFungibleLocalId>(
            NonFungibleLocalId("${tokenOwner.nft.localId}")
        )
            ; `
    } else {
        manifest += `
        CALL_METHOD
        Address("${account}")
        "create_proof_of_amount"
        Address("${tokenOwner.token}")
        Decimal("1")
            ; `
    }

    return manifest + `

        CALL_METHOD
        Address("${account}")
        "withdraw"
        Address("${tokenAddress}")
        Decimal("${amount}")
            ;

        TAKE_ALL_FROM_WORKTOP
        Address("${tokenAddress}")
        Bucket("burn_bucket")
            ;

        BURN_RESOURCE
        Bucket("burn_bucket")
            ;
        `
}